import { portalStoreToken } from '@servicetitan/cp-ui';
import { useDependencies } from '@servicetitan/react-ioc';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { observer } from 'mobx-react';
import { FC, useState } from 'react';
import { FeatureFlagKeys } from '../../../../feature-flags';
import { BannerMessage } from '../../../common/components/banner-message';
import { WebPortalStore } from '../../../common/stores/portal.store';
import {
    bannerDismissKey,
    dismissBanner,
    isBannerDismissed,
} from '../../../common/utils/banner-dismissal';
import { BannerStyleStatus } from '../../../common/utils/banner-settings';
import { SignInBannerSettingsStore } from '../../../dashboard/api/sign-in-banner-settings.store';
import * as Styles from './sign-in-banner.module.less';

export interface SignInBannerProps {
    style: BannerStyleStatus;
}

export const SignInBanner: FC<SignInBannerProps> = observer(({ style }) => {
    const { enableCustomBanners } = useFlags<FeatureFlagKeys>();
    const [{ settings }] = useDependencies<[WebPortalStore]>(portalStoreToken as any);
    const [bannerStore] = useDependencies(SignInBannerSettingsStore);
    const [closedThisLoad, setClosedThisLoad] = useState(false);

    if (!enableCustomBanners || closedThisLoad || !bannerStore.shouldShow) {
        return null;
    }

    const banner = bannerStore.value;
    if (banner?.style !== style) {
        return null;
    }

    const dismissKey = bannerDismissKey({
        host: settings?.host,
        scope: 'signin',
        version: banner.version,
    });

    if (isBannerDismissed(dismissKey)) {
        return null;
    }

    const onClose = () => {
        dismissBanner({
            host: settings?.host,
            scope: 'signin',
            version: banner.version,
        });
        setClosedThisLoad(true);
    };

    return (
        <div
            className={classNames(Styles.banner, {
                [Styles.announcement]: style === BannerStyleStatus.Announcement,
                [Styles.alert]: style === BannerStyleStatus.Alert,
            })}
            data-banner-style={style === BannerStyleStatus.Announcement ? 'announcement' : 'alert'}
            data-cy="signin-banner"
        >
            <BannerMessage
                color={banner.color}
                messageHtml={banner.messageHtml}
                onClose={onClose}
                style={banner.style}
            />
        </div>
    );
});
